<template>
    <p-modal
        :button-label="buttonLabel"
        size="lg"
        :label="pageTitle"
        ref="addRentals"
        @ok.prevent="handleModalOk"
        :button-variant="buttonVariant"
        :disable-button="disabled"
        @shown="assignRentalData"
    >
        <template v-slot:default>
            <p-form ref="form">
                <b-row>
                    <b-col cols="6">
                        <p-select
                            :options="rentalOptions"
                            label="Rental Source"
                            v-model="rentalsData.rentalSource"
                            :disabled="
                                rentalsData.newRentalItemPeriodId != null ||
                                rentalsData.usedRentalItemPeriodId != null
                            "
                        />
                    </b-col>
                    <b-col cols="6">
                        <p-number
                            label="Rebate Price"
                            input-type="money"
                            v-model="rentalsData.rebatePrice"
                            rules="min_value:0"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="6">
                        <p-input 
                            label="Author/Title/ISBN"
                            disabled 
                            :model-value="rentalsData.author + ' / ' + rentalsData.title + ' / ' + rentalsData.isbn"
                            />
                    </b-col>
                    <b-col cols="6">
                        <p-number
                            label="Late Fee"
                            input-type="money"
                            v-model="rentalsData.lateFee"
                            :rules="isRequired + '|min_value:0'"
                            name="Late Fee"
                        />
                    </b-col>
                </b-row>
                <b-row class="pt-1">
                    <b-col>
                        <p-table
                            :items="[rentalsData]"
                            :fields="rentalFields"
                            :enableRowSelection="false"
                            :showRecordCount="false"
                        >
                            <template
                                v-slot:cell(bookVariant)="{
                                    value,
                                    item
                                }"
                            >
                                <div class="rental-cell">
                                    <div class="rental-cell-item">New</div>
                                    <div class="rental-cell-item" v-if="item.usedSku != null">Used</div>
                                </div>
                            </template>
                            <template
                                v-slot:cell(newRentalPrice)="{
                                    value,
                                    item
                                }"
                            >
                                <div class="rental-cell">
                                    <div class="rental-cell-item">
                                        <p-number
                                            input-type="money"
                                            v-model="item.newRentalPrice"
                                            :rules="isRequired + '|min_value:0'"
                                            name="New Rental Price"
                                            compact-format
                                        />
                                    </div>
                                    <div class="rental-cell-item" v-if="item.usedSku != null">
                                        <p-number
                                            input-type="money"
                                            v-model="item.usedRentalPrice"
                                            :rules="isRequired + '|min_value:0'"
                                            name="Used Rental Price"
                                            compact-format
                                        />
                                    </div>
                                </div>
                            </template>
                            <template
                                v-slot:cell(newPrice)="{
                                    value,
                                    item
                                }"
                            >
                                <div class="rental-cell">
                                    <div class="rental-cell-item">
                                        {{ currencyFormatter(item.newPrice) }}
                                    </div>
                                    <div class="rental-cell-item" v-if="item.usedSku != null">
                                        {{ currencyFormatter(item.usedPrice) }}
                                    </div>
                                </div>
                            </template>
                            <template
                                v-slot:cell(newCost)="{
                                    value,
                                    item
                                }"
                            >
                                <div class="rental-cell">
                                    <div class="rental-cell-item">
                                        {{ currencyFormatter(item.newCost) }}
                                    </div>
                                    <div class="rental-cell-item" v-if="item.usedSku != null">
                                        {{ currencyFormatter(item.usedCost) }}
                                    </div>
                                </div>
                            </template>
                            <template
                                v-slot:cell(newIsActive)="{
                                    value,
                                    item
                                }"
                            >
                                <div class="rental-cell">
                                    <div class="rental-cell-item">
                                        <p-checkbox
                                            :use-switch="true"
                                            v-model="item.newIsActive"
                                            size="lg"
                                        ></p-checkbox>
                                    </div>
                                    <div class="rental-cell-item" v-if="item.usedSku != null">
                                        <p-checkbox
                                            :use-switch="true"
                                            v-model="item.usedIsActive"
                                            size="lg"
                                        ></p-checkbox>
                                    </div>
                                </div>
                            </template>
                        </p-table>
                    </b-col>
                </b-row>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok }">
            <b-row class="w-100">
                <b-col class="mb-3 text-center">
                    <p-button
                        :is-busy="isBusy"
                        variant="primary"
                        @click="ok"
                        class="ms-2"
                        :disabled="rentalsData.rentalSource == 'None'"
                        >Save</p-button
                    >
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import { currencyFormatter } from '@/components/Common/Formatters.js';
export default {
    props: {
        rentals: {
            type: Object,
            default: () => null
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    created: function() {
        this.rentalsData = JSON.parse(JSON.stringify(this.rentals));
    },
    data() {
        return {
            isBusy: false,
            rentalsData: [],
            rentalOptions: [
                { value: 'Store Managed', text: 'Store Managed' },
                { value: 'None', text: 'None' }
            ],
            rentalFields: [
                {
                    label: '',
                    key: 'bookVariant'
                },
                {
                    key: 'newRentalPrice',
                    label: 'Rental Price',
                    formatter: currencyFormatter
                },
                {
                    key: 'newPrice',
                    label: 'Price',
                    formatter: currencyFormatter
                },
                {
                    key: 'newCost',
                    label: 'Cost',
                    formatter: currencyFormatter
                },
                {
                    key: 'newIsActive',
                    label: 'Status'
                }
            ]
        };
    },
    computed: {
        buttonLabel() {
            return 'Edit';
        },
        pageTitle() {
            return 'Edit Rentals';
        },
        buttonVariant() {
            return 'outline-primary';
        },
        isRequired() {
            return this.rentalsData.newIsActive || this.rentalsData.usedIsActive
                ? 'required'
                : '';
        }
    },
    methods: {
        currencyFormatter: currencyFormatter,
        assignRentalData(){
            this.rentalsData = JSON.parse(JSON.stringify(this.rentals));
        },
        handleModalOk: async function() {
            if (!(await this.$refs.form.validate())) {
                return;
            }

            axios.post('rentals/saverentals', this.rentalsData).then(() => {
                this.$emit('data-saved');
                this.$refs.addRentals.hide();
                this.$appSuccess(`Rental saved successfully.`)
            });

            
        }
    }
};
</script>

<style scoped lang="scss">
.rental-cell {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}
.rental-cell-item {
    min-height: 2.5rem;
    display: flex;
    align-items: center;
}
:deep(.rental-cell-item .form-group) {
    margin-bottom: 0;
    width: 100%;
}
</style>
